import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import EventSourcePolyfillModule from 'event-source-polyfill';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppSelector } from 'store/hook';
import { selectAssociatedAssets } from 'store/asset.slice';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import DispatchMonitorAPI, {
  type DispatchDeviceLatest,
} from 'api/dispatch-monitor.api';
import LocationImporterAPI from 'api/location-importer.api';
import DeviceAPI from 'api/device.api';
import GradeImporterAPI from 'api/grade-importer.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import { utmToLatLon } from 'utils/utmToLatLon';

type EventSourcePolyfillType = new (
  url: string,
  init?: { headers?: Record<string, string> }
) => EventSource;

const EventSourcePolyfill = (EventSourcePolyfillModule as any)
  .EventSourcePolyfill as EventSourcePolyfillType;

interface AssetInfo {
  bumperNumber: string;
  symbotId: string;
  assetType: string;
}

// Partial because a device can arrive via TRIP_START (only routeFrom/routeTo)
// before it's ever sent a full PLAN_KPI_RECALCULATED payload.
type DevicePayload = Partial<DispatchDeviceLatest['payload']>;

type NodeKind = 'loading' | 'crusher' | 'dumping' | 'other';

type TruckStatusBucket =
  | 'hauling'
  | 'travelling'
  | 'spotting'
  | 'queued'
  | 'down'
  | 'other';

const BUCKET_COLORS: Record<TruckStatusBucket, string> = {
  hauling: '#f5a524',
  travelling: '#38bdf8',
  spotting: '#34d399',
  queued: '#94a3b8',
  down: '#f87171',
  other: '#94a3b8',
};

const NODE_CHIP_COLORS: Record<NodeKind, string> = {
  loading: '#85b6ff',
  crusher: '#f6c177',
  dumping: '#c4b1fb',
  other: '#aebacb',
};

const bucketOf = (statusRaw: string): TruckStatusBucket => {
  const s = statusRaw?.toLowerCase() ?? '';
  if (s === 'hauling' || s === 'dumping') return 'hauling';
  if (s === 'travelling' || s === 'traveling') return 'travelling';
  if (s === 'spotting' || s === 'loading') return 'spotting';
  if (s === 'queued' || s === 'queuing' || s === 'queing') return 'queued';
  if (s === 'down' || s === 'maintenance') return 'down';
  return 'other';
};

interface LocationPoint {
  locationType: string;
  lat: number | null;
  lon: number | null;
}

// Names for the same place show up with different punctuation across
// systems — "GGN-Mud" vs "GGN_MUD" vs "GGN MUD" vs "ggnmud". Strip
// everything but letters/digits before comparing so those all match.
const normalizeName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Exact (punctuation/case-normalized) lookup only — no prefix/substring
// guessing. A route name is resolved to a real Location Importer record by:
//  1. Treating it as a Grade name (routeFrom/routeTo is often the grade,
//     e.g. "GGN-Mud") — find that grade, then look up ITS locationName.
//  2. Otherwise treating it as a location name directly.
// Either way, the final answer always comes from that location's own
// registered locationType — never inferred from the name string itself.
const findByNormalizedName = <T,>(
  map: Record<string, T>,
  name: string
): T | undefined => {
  if (map[name]) return map[name];
  const target = normalizeName(name);
  for (const [key, value] of Object.entries(map)) {
    if (normalizeName(key) === target) return value;
  }
  return undefined;
};

const resolveLocationPoint = (
  locationByName: Record<string, LocationPoint>,
  grades: GradeRecord[],
  name: string
): LocationPoint | undefined => {
  const target = normalizeName(name);
  const grade = grades.find((g) => g.name && normalizeName(g.name) === target);
  if (grade?.locationName) {
    const viaGrade = findByNormalizedName(locationByName, grade.locationName);
    if (viaGrade) return viaGrade;
  }
  return findByNormalizedName(locationByName, name);
};

const nodeKindOf = (
  locationByName: Record<string, LocationPoint>,
  grades: GradeRecord[],
  name: string
): NodeKind => {
  const lt =
    resolveLocationPoint(
      locationByName,
      grades,
      name
    )?.locationType?.toLowerCase() ?? '';
  if (lt === 'loading') return 'loading';
  if (lt === 'crusher') return 'crusher';
  if (lt === 'dumping' || lt === 'stockpile') return 'dumping';
  return 'other';
};

// Great-circle distance in meters between two lat/lon points
const haversineMeters = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Truck's % progress along the from->to route, using real GPS position when
// available; falls back to the shift-completion percentage otherwise.
interface RouteProgressResult {
  progress: number;
  // Straight-line distances between the truck's live GPS fix and each end
  // of the route — both null whenever we fall back to the shift-completion
  // % (no real position/coords to measure from).
  distanceLeftMeters: number | null;
  distanceTravelledMeters: number | null;
}

const routeProgress = (
  from: LocationPoint | undefined,
  to: LocationPoint | undefined,
  pos: { lat: number; lon: number } | undefined,
  fallback: number,
  debugLabel?: string
): RouteProgressResult => {
  if (
    from?.lat == null ||
    from?.lon == null ||
    to?.lat == null ||
    to?.lon == null ||
    pos == null
  ) {
    if (debugLabel) {
      console.warn(
        `[assignment-board] ${debugLabel} — distance unknown (missing: ${[
          from?.lat == null || from?.lon == null ? 'from-coords' : null,
          to?.lat == null || to?.lon == null ? 'to-coords' : null,
          pos == null ? 'gps-position' : null,
        ]
          .filter(Boolean)
          .join(', ')})`,
        { from, to, pos }
      );
    }
    return {
      progress: fallback,
      distanceLeftMeters: null,
      distanceTravelledMeters: null,
    };
  }
  // distFromStart / totalDist blows past 100% the moment the truck's real
  // GPS fix isn't sitting exactly on the straight line between from/to (or
  // the route is short) — clamping then falsely pins it near the
  // destination. Using distance-to-start vs distance-to-end instead
  // self-normalizes to [0, 100] regardless of how far off-line the fix is.
  const distFromStart = haversineMeters(from.lat, from.lon, pos.lat, pos.lon);
  const distToEnd = haversineMeters(pos.lat, pos.lon, to.lat, to.lon);
  const total = distFromStart + distToEnd;
  if (total <= 0) {
    return {
      progress: fallback,
      distanceLeftMeters: null,
      distanceTravelledMeters: null,
    };
  }
  return {
    progress: Math.min(98, Math.max(2, (distFromStart / total) * 100)),
    distanceLeftMeters: distToEnd,
    distanceTravelledMeters: distFromStart,
  };
};

const formatMeters = (meters: number): string =>
  meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toFixed(1)} km`;

const formatTruckTooltip = (
  t: ReturnType<typeof useTranslation>['t'],
  distanceLeftMeters: number | null,
  distanceTravelledMeters: number | null
): string => {
  if (distanceLeftMeters == null || distanceTravelledMeters == null) {
    return t('assignment_board_distance_unknown');
  }
  return `${t('assignment_board_distance_left', {
    value: formatMeters(distanceLeftMeters),
  })} · ${t('assignment_board_distance_travelled', {
    value: formatMeters(distanceTravelledMeters),
  })}`;
};

interface RouteTruck {
  bumperNumber: string;
  bucket: TruckStatusBucket;
  progress: number;
  distanceLeftMeters: number | null;
  distanceTravelledMeters: number | null;
}

interface RouteRow {
  key: string;
  from: string;
  fromKind: NodeKind;
  to: string;
  toKind: NodeKind;
  excavator: string | null;
  trucks: RouteTruck[];
}

interface BoardGroup {
  name: string;
  routes: RouteRow[];
  truckCount: number;
}

interface NearbyTruckProximity {
  bumperNumber: string;
  distance_m: number;
  gps_velocity_value?: string;
  gps_angle_value?: string;
}

interface ExcavatorProximity {
  bumperNumber: string;
  nearby_trucks: NearbyTruckProximity[];
  truck_count: number;
  computed_at: number;
}

interface LocationCongestion {
  locationId: string;
  name: string;
  locationType: string;
  truckCount: number;
  trucksCapacity: number;
  congested: boolean;
  trucks: string[];
  computedAt: number;
}

// One column per excavator (proximity) or location (congestion) in the
// combined table below — each column just lists its own trucks top to
// bottom, independent of how many rows every other column has.
interface MatrixColumn {
  type: 'excavator' | 'location';
  key: string;
  label: string;
  trucks: string[];
  congested?: boolean;
  truckCount?: number;
  trucksCapacity?: number;
}

const AssignmentBoardInner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enableDarkTheme } = useAppTheme();
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const { customerCode } = useCustomerInfo();
  const storeAssets = useAppSelector(selectAssociatedAssets);
  const { proj4String, localOriginEasting, localOriginNorthing } =
    useAppSelector((s) => s.persistedReducer.customer);

  const dispatchApi = useRef(new DispatchMonitorAPI()).current;
  const locationApi = useRef(new LocationImporterAPI()).current;
  const deviceApi = useRef(new DeviceAPI()).current;
  const gradeApi = useRef(new GradeImporterAPI()).current;

  const [assets, setAssets] = useState<AssetInfo[]>([]);
  const [locationByName, setLocationByName] = useState<
    Record<string, LocationPoint>
  >({});
  const [grades, setGrades] = useState<GradeRecord[]>([]);
  const [devicePayloads, setDevicePayloads] = useState<
    Record<string, DevicePayload>
  >({});
  const [assetPositions, setAssetPositions] = useState<
    Record<string, { lat: number; lon: number }>
  >({});
  // Asset name reported straight off the dispatch payload (msg.asset) — used
  // as the on-chip label so the board shows a real asset name instead of
  // the raw symbotId when there's no matching entry in getAssetInfo.
  const [assetNames, setAssetNames] = useState<Record<string, string>>({});
  // Live excavator->nearby-trucks proximity, keyed by the excavator's
  // bumperNumber lowercased (matches how RouteRow.excavator is compared).
  const [proximityByExcavator, setProximityByExcavator] = useState<
    Record<string, ExcavatorProximity>
  >({});
  // Live per-location congestion, keyed by locationId.
  const [congestionByLocation, setCongestionByLocation] = useState<
    Record<string, LocationCongestion>
  >({});
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const panelBg = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;
  const borderColor = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.08)
    : theme.palette.divider;
  const textPrimary = theme.palette.text.primary;
  const textMuted = theme.palette.text.secondary;

  // Each fetch below is independent — a single hanging/erroring call (no
  // timeout is configured on the axios instance anywhere in this app) must
  // never block the others from updating their own piece of state.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const assetJson = await dispatchApi.getAssetInfo();
        if (cancelled) return;
        setAssets(assetJson.assets ?? []);
      } catch (err) {
        console.error('[assignment-board] getAssetInfo failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatchApi, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const locationsJson = await locationApi.getLocations();
        if (cancelled) return;
        const locMap: Record<string, LocationPoint> = {};
        locationsJson.forEach((l) => {
          let lat = l.centroidLat;
          let lon = l.centroidLon;
          // centroidLat/Lon are optional and often unset — most locations
          // only have surveyed easting/northing (UTM), so convert those to
          // lat/lon instead of leaving this location coordinate-less.
          if (
            (lat == null || lon == null || lat === 0 || lon === 0) &&
            Number.isFinite(l.easting) &&
            Number.isFinite(l.northing)
          ) {
            const converted = utmToLatLon(
              l.easting,
              l.northing,
              proj4String,
              localOriginEasting,
              localOriginNorthing
            );
            if (converted) {
              lat = converted.latitude;
              lon = converted.longitude;
            }
          }
          locMap[l.name] = {
            locationType: l.locationType,
            lat: lat ?? null,
            lon: lon ?? null,
          };
        });
        setLocationByName(locMap);
      } catch (err) {
        console.error('[assignment-board] getLocations failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    locationApi,
    refreshKey,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
  ]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const gradesJson = await gradeApi.getGrades();
        if (cancelled) return;
        setGrades(gradesJson);
      } catch (err) {
        console.error('[assignment-board] getGrades failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gradeApi, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const tripsJson = await dispatchApi.getTrips();
        if (cancelled) return;
        const payloadMap: Record<string, DevicePayload> = {};
        const nameMap: Record<string, string> = {};
        Object.entries(tripsJson?.trips ?? {}).forEach(([deviceId, trip]) => {
          if (trip?.payload) {
            payloadMap[deviceId] = {
              routeFrom: trip.payload.routeFrom,
              routeTo: trip.payload.routeTo,
              excavator: trip.payload.excavator,
              nextFrom: trip.payload.nextFrom,
              nextTo: trip.payload.nextTo,
            };
          }
          if (trip?.asset) nameMap[deviceId] = trip.asset;
        });
        setDevicePayloads(payloadMap);
        setAssetNames((prev) => ({ ...prev, ...nameMap }));
      } catch (err) {
        console.error('[assignment-board] getTrips failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatchApi, refreshKey]);

  // Initial proximity/congestion snapshot — the SSE channels only carry
  // updates from the moment the connection opens, so this seeds the tables
  // with whatever was last computed instead of waiting for the next event.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const snapshot = await dispatchApi.getLatestProximityCongestion();
        if (cancelled || !snapshot || Array.isArray(snapshot)) return;
        const proximityMap: Record<string, ExcavatorProximity> = {};
        Object.values(snapshot.proximity ?? {}).forEach((p) => {
          if (!p?.bumperNumber) return;
          proximityMap[p.bumperNumber.toLowerCase()] = {
            bumperNumber: p.bumperNumber,
            nearby_trucks: p.nearby_trucks ?? [],
            truck_count: p.truck_count ?? 0,
            computed_at: p.computed_at ?? 0,
          };
        });
        // Merge under any SSE data that may have already arrived — a live
        // update should never be clobbered by this one-time snapshot fetch.
        setProximityByExcavator((prev) => ({ ...proximityMap, ...prev }));

        const congestionMap: Record<string, LocationCongestion> = {};
        Object.values(snapshot.congestion ?? {}).forEach((c) => {
          if (!c?.locationId) return;
          congestionMap[c.locationId] = {
            locationId: c.locationId,
            name: c.name,
            locationType: c.locationType,
            truckCount: c.truckCount ?? 0,
            trucksCapacity: c.trucksCapacity ?? 0,
            congested: Boolean(c.congested),
            trucks: c.trucks ?? [],
            computedAt: c.computedAt ?? 0,
          };
        });
        setCongestionByLocation((prev) => ({ ...congestionMap, ...prev }));
      } catch (err) {
        console.error(
          '[assignment-board] getLatestProximityCongestion failed:',
          err
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatchApi, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const sensorJson = await deviceApi.deviceLatestSensorDataAll(true);
        if (cancelled) return;
        const posMap: Record<string, { lat: number; lon: number }> = {};
        Object.entries(sensorJson ?? {}).forEach(([deviceId, s]: any) => {
          const latRaw = s?.gps_latitude ?? s?.latitude ?? s?.lat;
          const lonRaw = s?.gps_longitude ?? s?.longitude ?? s?.lon;
          const lat = typeof latRaw === 'number' ? latRaw : parseFloat(latRaw);
          const lon = typeof lonRaw === 'number' ? lonRaw : parseFloat(lonRaw);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            posMap[deviceId] = { lat, lon };
          }
        });
        setAssetPositions((prev) => ({ ...prev, ...posMap }));
      } catch (err) {
        console.error(
          '[assignment-board] deviceLatestSensorDataAll failed:',
          err
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deviceApi, refreshKey]);

  // Live GPS + dispatch KPI updates — both arrive over the same
  // live-dispatch-sse stream, split by channel (control-room:* for
  // gps_latitude/gps_longitude, xtab:* for PLAN_KPI_RECALCULATED).
  useEffect(() => {
    if (!accessToken || !customerId) return;
    const es = new EventSourcePolyfill(
      dispatchApi.liveDispatchSSEUrl(customerId),
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    es.onmessage = (e: MessageEvent) => {
      const raw = e.data as string;
      // Initial connection handshake frame is plain text (e.g. "connected,"),
      // not JSON — skip it quietly instead of parsing and erroring on it.
      const trimmed = raw?.trim();
      if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
        return;
      }
      try {
        const outer = JSON.parse(raw) as {
          channel: string;
          message: string | Record<string, any>;
        };

        // Some frames arrive double-enveloped — message is itself a
        // stringified { channel, message } wrapper around the real payload.
        // Unwrap until we hit an object that isn't just another envelope.
        const unwrap = (val: any): any => {
          const parsed = typeof val === 'string' ? JSON.parse(val) : val;
          if (
            parsed &&
            typeof parsed === 'object' &&
            'message' in parsed &&
            !('action' in parsed)
          ) {
            return unwrap(parsed.message);
          }
          return parsed;
        };

        // Match on prefix/suffix, not an exact customerCode string — if
        // customerCode hasn't hydrated yet (or differs even slightly) an
        // exact-match check silently drops every GPS message with no error.
        if (
          outer.channel?.startsWith('control-room:') &&
          outer.channel.endsWith(':pubsub')
        ) {
          const msg = unwrap(outer.message);
          const rawDeviceId = (msg?.deviceId ?? msg?.device) as
            | string
            | undefined;
          const deviceId = rawDeviceId?.trim();
          const lat = msg?.gps_latitude;
          const lon = msg?.gps_longitude;
          if (deviceId && lat != null && lon != null) {
            setAssetPositions((prev) => ({
              ...prev,
              [deviceId]: { lat: Number(lat), lon: Number(lon) },
            }));
          }
          return;
        }

        // Excavator<->nearby-trucks proximity — one message per excavator,
        // matched to a RouteRow by bumperNumber (RouteRow.excavator is a
        // bumperNumber-like string, same as truck bumper labels elsewhere
        // on this board).
        if (
          outer.channel?.startsWith('proximity:') &&
          outer.channel.endsWith(':pubsub')
        ) {
          const unwrapped = unwrap(outer.message);
          // The stream sends either one excavator's payload or a batched
          // array of them in the same message — normalize to a list.
          const entries: any[] = Array.isArray(unwrapped)
            ? unwrapped
            : [unwrapped];
          const updates: Record<string, ExcavatorProximity> = {};
          entries.forEach((msg) => {
            const bumper = (msg?.bumperNumber as string | undefined)?.trim();
            if (!bumper) return;
            const nearbyTrucks: NearbyTruckProximity[] = Array.isArray(
              msg?.nearby_trucks
            )
              ? msg.nearby_trucks.map((nt: any) => ({
                  bumperNumber: nt?.bumperNumber ?? '',
                  distance_m: Number(nt?.distance_m ?? 0),
                  gps_velocity_value: nt?.gps_velocity_value,
                  gps_angle_value: nt?.gps_angle_value,
                }))
              : [];
            updates[bumper.toLowerCase()] = {
              bumperNumber: bumper,
              nearby_trucks: nearbyTrucks,
              truck_count: Number(msg?.truck_count ?? nearbyTrucks.length),
              computed_at: Number(msg?.computed_at ?? 0),
            };
          });
          if (Object.keys(updates).length > 0) {
            setProximityByExcavator((prev) => ({ ...prev, ...updates }));
          }
          return;
        }

        // Per-location congestion — one message per location, same
        // single-or-batched-array shape as the proximity channel above.
        if (
          outer.channel?.startsWith('congestion:') &&
          outer.channel.endsWith(':pubsub')
        ) {
          const unwrapped = unwrap(outer.message);
          const entries: any[] = Array.isArray(unwrapped)
            ? unwrapped
            : [unwrapped];
          const updates: Record<string, LocationCongestion> = {};
          entries.forEach((msg) => {
            const locationId = (msg?.locationId as string | undefined)?.trim();
            if (!locationId) return;
            updates[locationId] = {
              locationId,
              name: msg?.name ?? locationId,
              locationType: msg?.locationType ?? '',
              truckCount: Number(msg?.truckCount ?? 0),
              trucksCapacity: Number(msg?.trucksCapacity ?? 0),
              congested: Boolean(msg?.congested),
              trucks: Array.isArray(msg?.trucks) ? msg.trucks : [],
              computedAt: Number(msg?.computedAt ?? 0),
            };
          });
          if (Object.keys(updates).length > 0) {
            setCongestionByLocation((prev) => ({ ...prev, ...updates }));
          }
          return;
        }

        // TRIP_COMPLETED has shown up on both the last:pubsub and the plain
        // pubsub channel — handle it by action, not by channel, so it's
        // never silently dropped regardless of which one it arrives on.
        const handleTripCompleted = (msg: any): boolean => {
          if (msg?.action !== 'TRIP_COMPLETED') return false;
          const device = (msg?.device as string | undefined)?.trim();
          const routeFrom = msg?.payload?.routeFrom as string | undefined;
          const routeTo = msg?.payload?.routeTo as string | undefined;
          if (device && routeFrom && routeTo) {
            const routeKey = `${routeFrom}__${routeTo}`;
            setDeviceRouteHistory((prev) => {
              const existing = prev[device];
              if (!existing?.[routeKey]) return prev;
              const { [routeKey]: _removed, ...rest } = existing;
              return { ...prev, [device]: rest };
            });
            setDevicePayloads((prev) => {
              const current = prev[device];
              if (
                current?.routeFrom !== routeFrom ||
                current?.routeTo !== routeTo
              ) {
                return prev;
              }
              const { routeFrom: _rf, routeTo: _rt, ...rest } = current;
              return { ...prev, [device]: rest };
            });
          }
          return true;
        };

        // last:pubsub carries TRIP_START — a new trip's routeFrom/routeTo,
        // sent before the truck has any PLAN_KPI_RECALCULATED payload yet.
        // Merge just the route fields in, keep whatever else the device
        // already had (status/tonnage/etc. arrive later on the plain channel).
        if (outer.channel === `xtab:${customerCode}:last:pubsub`) {
          const msg = unwrap(outer.message);
          if (msg?.action === 'TRIP_START') {
            const device = (msg?.device as string | undefined)?.trim();
            const routeFrom = msg?.payload?.routeFrom as string | undefined;
            const routeTo = msg?.payload?.routeTo as string | undefined;
            const excavator = msg?.payload?.excavator as string | undefined;
            if (device && routeFrom && routeTo) {
              setDevicePayloads((prev) => ({
                ...prev,
                [device]: {
                  ...prev[device],
                  routeFrom,
                  routeTo,
                  ...(excavator && { excavator }),
                },
              }));
              const assetName = msg?.asset as string | undefined;
              if (assetName) {
                setAssetNames((prev) => ({ ...prev, [device]: assetName }));
              }
            }
          }
          handleTripCompleted(msg);
          return;
        }

        // Only skip the other known sub-channels — don't require an exact
        // match on the plain pubsub channel (mirrors DispatchMonitorView).
        if (
          outer.channel === `xtab:${customerCode}:trafficevents:pubsub` ||
          outer.channel === `xtab:${customerCode}:blend:pubsub`
        ) {
          return;
        }
        const msg = unwrap(outer.message);
        if (handleTripCompleted(msg)) return;
        // TRIP_STARTED carries the same full payload shape as
        // PLAN_KPI_RECALCULATED (truckStatus/routeFrom/routeTo/etc.) — treat
        // it the same instead of silently dropping the trip's first update.
        if (
          msg?.action !== 'PLAN_KPI_RECALCULATED' &&
          msg?.action !== 'TRIP_STARTED'
        ) {
          return;
        }
        const device = (msg?.device as string | undefined)?.trim();
        if (!device || !msg?.payload) return;
        setDevicePayloads((prev) => ({ ...prev, [device]: msg.payload }));
        const assetName = msg?.asset as string | undefined;
        if (assetName) {
          setAssetNames((prev) => ({ ...prev, [device]: assetName }));
        }
      } catch (err) {
        console.error('[assignment-board-sse] parse error:', err);
      }
    };
    es.onerror = () => {
      console.warn('[assignment-board-sse] connection error — reconnecting');
    };
    return () => {
      es.close();
    };
  }, [accessToken, customerId, customerCode, dispatchApi]);

  const assetBySymbotId = useMemo(() => {
    const map: Record<string, AssetInfo> = {};
    assets.forEach((a) => {
      map[a.symbotId] = a;
    });
    return map;
  }, [assets]);

  // Authoritative asset<->device association from the fleet roster (Redux) —
  // preferred over dispatchApi.getAssetInfo(), which can be missing devices.
  const assetByDeviceId = useMemo(() => {
    const map: Record<string, { bumperNumber?: string }> = {};
    storeAssets.forEach((a) => {
      const deviceId = a.device?.deviceId;
      if (deviceId) map[deviceId] = { bumperNumber: a.bumperNumber };
    });
    return map;
  }, [storeAssets]);

  // Every device that has pushed dispatch data shows up — no status/bucket
  // filtering, no dropping unrecognised truckStatus values.
  const trucks = useMemo(
    () =>
      Object.entries(devicePayloads).map(([deviceId, p]) => {
        const fallback = Math.min(
          98,
          Math.max(2, p?.completionPercentage ?? 50)
        );
        const routeFrom = p?.routeFrom
          ? p.routeFrom
          : t('assignment_board_unassigned');
        const routeTo = p?.routeTo
          ? p.routeTo
          : t('assignment_board_unassigned');
        const { progress, distanceLeftMeters, distanceTravelledMeters } =
          routeProgress(
            resolveLocationPoint(locationByName, grades, routeFrom),
            resolveLocationPoint(locationByName, grades, routeTo),
            assetPositions[deviceId],
            fallback,
            `${deviceId} (${routeFrom} -> ${routeTo})`
          );
        const rawBucket = bucketOf(p?.truckStatus ?? '');
        // The return leg (ending at a loading point) is always run empty —
        // a truck can't be "hauling" material while heading TO the loading
        // point to pick some up, whatever the raw backend truckStatus says.
        // Other real states (spotting/queued/down) still pass through as-is.
        const bucket: TruckStatusBucket =
          rawBucket === 'hauling' &&
          nodeKindOf(locationByName, grades, routeTo) === 'loading'
            ? 'travelling'
            : rawBucket;
        return {
          deviceId,
          bumperNumber:
            assetByDeviceId[deviceId]?.bumperNumber ??
            assetBySymbotId[deviceId]?.bumperNumber ??
            assetNames[deviceId] ??
            deviceId,
          bucket,
          routeFrom,
          routeTo,
          excavator: p?.excavator ?? null,
          progress,
          distanceLeftMeters,
          distanceTravelledMeters,
        };
      }),
    [
      devicePayloads,
      assetByDeviceId,
      assetBySymbotId,
      assetNames,
      locationByName,
      grades,
      assetPositions,
      t,
    ]
  );

  const kpiCounts = useMemo(() => {
    const counts: Record<TruckStatusBucket, number> = {
      hauling: 0,
      travelling: 0,
      spotting: 0,
      queued: 0,
      down: 0,
      other: 0,
    };
    trucks.forEach((tr) => {
      counts[tr.bucket] += 1;
    });
    return counts;
  }, [trucks]);

  // Each device's current (from->to) pair, keyed by routeKey so it's a
  // no-op to write the same value repeatedly. Holds at most one entry per
  // device — any other key gets pruned the moment the truck's live route
  // (from `trucks`) diverges from it, rather than waiting on a
  // TRIP_COMPLETED event (which can be missed) to remove the old one. The
  // route ROW itself must still never disappear from the UI just because no
  // truck is on it right now, so that's tracked separately below in
  // knownRoutes (grows only, never pruned).
  const [deviceRouteHistory, setDeviceRouteHistory] = useState<
    Record<string, Record<string, { from: string; to: string }>>
  >({});

  // Every from->to pair ever seen this session — seeds route rows so they
  // persist (empty) even after every truck has moved off them.
  const [knownRoutes, setKnownRoutes] = useState<
    Record<string, { from: string; to: string }>
  >({});

  useEffect(() => {
    setDeviceRouteHistory((prev) => {
      let changed = false;
      const next = { ...prev };
      trucks.forEach((tr) => {
        const routeKey = `${tr.routeFrom}__${tr.routeTo}`;
        const existing = next[tr.deviceId];
        const hasStaleKeys =
          existing && Object.keys(existing).some((k) => k !== routeKey);
        if (!existing?.[routeKey] || hasStaleKeys) {
          // A truck is only ever really on one route at a time — once its
          // live routeFrom/routeTo (from `trucks`, always authoritative)
          // diverges from what's stored, any OTHER key here is stale, not a
          // second concurrent leg. Don't wait on a TRIP_COMPLETED event
          // (which can be missed on reconnect or fail to exact-match) to
          // clear it — replace the bucket with just the current route so a
          // truck can never render "stuck" on a route it already left.
          next[tr.deviceId] = {
            [routeKey]: { from: tr.routeFrom, to: tr.routeTo },
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setKnownRoutes((prev) => {
      let changed = false;
      const next = { ...prev };
      trucks.forEach((tr) => {
        const routeKey = `${tr.routeFrom}__${tr.routeTo}`;
        if (!next[routeKey]) {
          next[routeKey] = { from: tr.routeFrom, to: tr.routeTo };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [trucks]);

  // One instance per device — deviceRouteHistory holds at most one route per
  // device (see above), so this just projects that single current route
  // together with the truck's current bucket/bumperNumber/excavator.
  const truckInstances = useMemo(() => {
    const currentByDevice = new Map(trucks.map((tr) => [tr.deviceId, tr]));
    const instances: Array<{
      bumperNumber: string;
      bucket: TruckStatusBucket;
      routeFrom: string;
      routeTo: string;
      excavator: string | null;
      progress: number;
      distanceLeftMeters: number | null;
      distanceTravelledMeters: number | null;
    }> = [];
    Object.entries(deviceRouteHistory).forEach(([deviceId, routes]) => {
      const current = currentByDevice.get(deviceId);
      if (!current) return;
      Object.values(routes).forEach(({ from, to }) => {
        const { progress, distanceLeftMeters, distanceTravelledMeters } =
          routeProgress(
            resolveLocationPoint(locationByName, grades, from),
            resolveLocationPoint(locationByName, grades, to),
            assetPositions[deviceId],
            current.progress,
            `${deviceId} (${from} -> ${to})`
          );
        // current.bucket already accounts for the empty-return-leg override
        // (see the `trucks` memo above) — no need to redo it here.
        instances.push({
          bumperNumber: current.bumperNumber,
          bucket: current.bucket,
          routeFrom: from,
          routeTo: to,
          excavator: current.excavator,
          progress,
          distanceLeftMeters,
          distanceTravelledMeters,
        });
      });
    });
    return instances;
  }, [deviceRouteHistory, trucks, locationByName, grades, assetPositions]);

  // Both directions are shown stacked on one screen (not tab-switched), so
  // this builds the same grouping the old single "tab" used, parameterized
  // by direction instead of reading it from state.
  const buildGroups = useCallback(
    (dir: 'haul' | 'travel'): BoardGroup[] => {
      const groupMap = new Map<string, Map<string, RouteRow>>();

      // Categorised strictly by real locationType, never guessed — both ends
      // must resolve to their expected kind, not just the loading end. Before
      // this, only the loading side was checked, so e.g. a workshop/parking/
      // fueling stop that happened to end at a loading point would show up
      // in "Dump -> Loading" alongside genuine dump legs. "Loading -> Dump"
      // shows routes starting AT a loading point and ending AT a dump;
      // "Dump -> Loading" shows the reverse (e.g. Dump_Ansal ->
      // BADSHAPUR_LOADING, the empty return leg). A route not matching both
      // ends shows in neither section.
      const matchesDirection = (from: string, to: string) =>
        dir === 'haul'
          ? nodeKindOf(locationByName, grades, from) === 'loading' &&
            nodeKindOf(locationByName, grades, to) === 'dumping'
          : nodeKindOf(locationByName, grades, from) === 'dumping' &&
            nodeKindOf(locationByName, grades, to) === 'loading';

      Object.entries(knownRoutes).forEach(([routeKey, { from, to }]) => {
        if (!matchesDirection(from, to)) return;
        if (!groupMap.has(from)) groupMap.set(from, new Map());
        const routeMap = groupMap.get(from);
        if (routeMap && !routeMap.has(routeKey)) {
          routeMap.set(routeKey, {
            key: routeKey,
            from,
            fromKind: nodeKindOf(locationByName, grades, from),
            to,
            toKind: nodeKindOf(locationByName, grades, to),
            excavator: null,
            trucks: [],
          });
        }
      });

      truckInstances.forEach((tr) => {
        if (!matchesDirection(tr.routeFrom, tr.routeTo)) return;
        const routeKey = `${tr.routeFrom}__${tr.routeTo}`;
        const routeMap = groupMap.get(tr.routeFrom);
        const route = routeMap?.get(routeKey);
        if (!route) return;
        route.trucks.push({
          bumperNumber: tr.bumperNumber,
          bucket: tr.bucket,
          progress: tr.progress,
          distanceLeftMeters: tr.distanceLeftMeters,
          distanceTravelledMeters: tr.distanceTravelledMeters,
        });
        if (tr.excavator && !route.excavator) route.excavator = tr.excavator;
      });

      return Array.from(groupMap.entries())
        .map(([name, routeMap]) => {
          const routes = Array.from(routeMap.values());
          return {
            name,
            routes,
            truckCount: routes.reduce((n, r) => n + r.trucks.length, 0),
          };
        })
        .sort((a, b) => b.truckCount - a.truckCount);
    },
    [truckInstances, knownRoutes, locationByName, grades]
  );

  const haulGroups = useMemo(() => buildGroups('haul'), [buildGroups]);
  const travelGroups = useMemo(() => buildGroups('travel'), [buildGroups]);

  const activeUnits = trucks.length;

  // Combined proximity/congestion table — one column per excavator or
  // location, each listing its own trucks top to bottom (nearest first for
  // excavators), independent of how many rows any other column has.
  const matrixColumns = useMemo<MatrixColumn[]>(
    () => [
      ...Object.values(proximityByExcavator).map(
        (p): MatrixColumn => ({
          type: 'excavator',
          key: `ex-${p.bumperNumber}`,
          label: p.bumperNumber,
          trucks: [...p.nearby_trucks]
            .sort((a, b) => a.distance_m - b.distance_m)
            .map((nt) => nt.bumperNumber)
            .filter(Boolean),
        })
      ),
      ...Object.values(congestionByLocation).map(
        (c): MatrixColumn => ({
          type: 'location',
          key: `loc-${c.locationId}`,
          label: c.name,
          trucks: c.trucks.filter(Boolean),
          congested: c.congested,
          truckCount: c.truckCount,
          trucksCapacity: c.trucksCapacity,
        })
      ),
    ],
    [proximityByExcavator, congestionByLocation]
  );

  const matrixRowCount = useMemo(
    () =>
      matrixColumns.reduce((max, col) => Math.max(max, col.trucks.length), 0),
    [matrixColumns]
  );

  // Shared group-list renderer — both stacked sections (Loading -> Dump,
  // Dump -> Loading) render identically, just fed a different group list.
  const renderRouteGroups = (list: BoardGroup[]): JSX.Element[] =>
    list.map((g) => (
      <Box
        key={g.name}
        sx={{
          bgcolor: panelBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 2.5,
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2.25,
            py: 1.5,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
              }}
            />
            <Typography
              fontSize={13}
              fontWeight={700}
              sx={{ color: textPrimary }}
            >
              {g.name}
            </Typography>
            <Typography fontSize={11} sx={{ color: textMuted }}>
              {g.routes.length === 1
                ? t('assignment_board_route_singular', {
                    count: g.routes.length,
                  })
                : t('assignment_board_route_plural', {
                    count: g.routes.length,
                  })}
            </Typography>
          </Box>
          <Typography fontSize={11.5} sx={{ color: textMuted }}>
            {t('assignment_board_truck_count', { count: g.truckCount })}
          </Typography>
        </Box>

        {g.routes.map((r) => {
          // Excavator is shown on whichever side is the actual loading
          // point — the FROM side on a haul leg (starts at loading), the
          // TO side on a return leg (ends at loading), never both.
          const excavatorOnFrom = Boolean(
            r.excavator && r.fromKind === 'loading'
          );
          const excavatorOnTo = Boolean(
            r.excavator && !excavatorOnFrom && r.toKind === 'loading'
          );
          return (
            <Box
              key={r.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: '160px minmax(150px, 1fr) 160px',
                alignItems: 'center',
                gap: 1.5,
                px: 2.25,
                py: 1.75,
                borderTop: `1px solid ${borderColor}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.25,
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    fontSize: excavatorOnFrom ? 14 : 12,
                    fontWeight: 700,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                    color: NODE_CHIP_COLORS[r.fromKind],
                    bgcolor: alpha(NODE_CHIP_COLORS[r.fromKind], 0.14),
                    border: `1px solid ${alpha(
                      NODE_CHIP_COLORS[r.fromKind],
                      0.4
                    )}`,
                  }}
                >
                  {excavatorOnFrom ? r.excavator : r.from}
                </Box>
                {excavatorOnFrom && (
                  <Typography
                    fontSize={10.5}
                    sx={{ color: textMuted, pl: 0.25 }}
                  >
                    {r.from}
                  </Typography>
                )}
              </Box>

              <Box sx={{ position: 'relative', height: 40 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 4,
                    right: 12,
                    top: '50%',
                    height: 2,
                    transform: 'translateY(-50%)',
                    bgcolor: alpha(theme.palette.text.secondary, 0.2),
                  }}
                />
                {r.trucks.map((tr) => (
                  <Tooltip
                    key={tr.bumperNumber}
                    title={formatTruckTooltip(
                      t,
                      tr.distanceLeftMeters,
                      tr.distanceTravelledMeters
                    )}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: `${tr.progress}%`,
                        transform: 'translate(-50%, -50%)',
                        transition: 'left 0.6s ease',
                        zIndex: 2,
                        whiteSpace: 'nowrap',
                        px: 1.1,
                        py: 0.4,
                        borderRadius: 1.5,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: '#fff',
                        bgcolor: alpha(BUCKET_COLORS[tr.bucket], 0.85),
                        border: `1px solid ${alpha(
                          BUCKET_COLORS[tr.bucket],
                          0.5
                        )}`,
                      }}
                    >
                      {tr.bumperNumber}
                    </Box>
                  </Tooltip>
                ))}
                {r.trucks.length === 0 && (
                  <Typography
                    fontSize={11}
                    sx={{
                      color: textMuted,
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('assignment_board_route_empty')}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.25,
                  justifySelf: 'end',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1.5,
                    fontSize: excavatorOnTo ? 14 : 12,
                    fontWeight: excavatorOnTo ? 700 : 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                    textAlign: 'right',
                    color: NODE_CHIP_COLORS[r.toKind],
                    bgcolor: alpha(NODE_CHIP_COLORS[r.toKind], 0.14),
                    border: `1px solid ${alpha(
                      NODE_CHIP_COLORS[r.toKind],
                      0.4
                    )}`,
                  }}
                >
                  {excavatorOnTo ? r.excavator : r.to}
                </Box>
                {excavatorOnTo && (
                  <Typography
                    fontSize={10.5}
                    sx={{ color: textMuted, pr: 0.25 }}
                  >
                    {r.to}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    ));

  const renderSection = (label: string, list: BoardGroup[]): JSX.Element => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        fontSize={14}
        fontWeight={700}
        sx={{ color: textPrimary, mb: 1.25 }}
      >
        {label}
      </Typography>
      {(!loading || trucks.length > 0) && list.length === 0 && (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: panelBg,
            border: `1px solid ${borderColor}`,
            borderRadius: 2.5,
            color: textMuted,
            fontSize: 13,
          }}
        >
          {t('assignment_board_no_data')}
        </Box>
      )}
      {(!loading || trucks.length > 0) && renderRouteGroups(list)}
    </Box>
  );

  return (
    <Box sx={{ p: 3, pb: 6, minHeight: 'calc(100vh - 115px)' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
            {t('assignment_board_title')}
          </Typography>
          <Typography fontSize={12.5} sx={{ color: textMuted, mt: 0.5 }}>
            {t('assignment_board_subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('assignment_board_refresh')}>
          <IconButton
            size="small"
            onClick={() => {
              setRefreshKey((k) => k + 1);
            }}
            sx={{
              border: `1px solid ${borderColor}`,
              color: textMuted,
              borderRadius: 2.5,
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* KPI strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {(
          [
            {
              label: t('assignment_board_kpi_active'),
              value: activeUnits,
              color: theme.palette.primary.main,
            },
            {
              label: t('assignment_board_kpi_hauling'),
              value: kpiCounts.hauling,
              color: BUCKET_COLORS.hauling,
            },
            {
              label: t('assignment_board_kpi_travelling'),
              value: kpiCounts.travelling,
              color: BUCKET_COLORS.travelling,
            },
            {
              label: t('assignment_board_kpi_spotting'),
              value: kpiCounts.spotting,
              color: BUCKET_COLORS.spotting,
            },
            {
              label: t('assignment_board_kpi_queued'),
              // Includes 'other' — devices with no truckStatus yet (e.g. a
              // truck that's only sent TRIP_START so far) so this tile plus
              // Hauling/Travelling/Spotting always adds up to Active Units.
              value: kpiCounts.queued + kpiCounts.down + kpiCounts.other,
              color: BUCKET_COLORS.queued,
            },
          ] as const
        ).map((kpi) => (
          <Box
            key={kpi.label}
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: panelBg,
              border: `1px solid ${borderColor}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: kpi.color,
                }}
              />
              <Typography
                fontSize={10.5}
                fontWeight={700}
                letterSpacing="0.08em"
                sx={{ color: textMuted, textTransform: 'uppercase' }}
              >
                {kpi.label}
              </Typography>
            </Box>
            <Typography
              fontSize={24}
              fontWeight={700}
              sx={{ color: kpi.color, mt: 1 }}
            >
              {kpi.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Legend — both directions now render stacked below instead of
          behind a tab, so there's no toggle here anymore */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {(
            [
              {
                label: t('assignment_board_legend_hauling'),
                color: BUCKET_COLORS.hauling,
              },
              {
                label: t('assignment_board_legend_travelling'),
                color: BUCKET_COLORS.travelling,
              },
              {
                label: t('assignment_board_legend_spotting'),
                color: BUCKET_COLORS.spotting,
              },
              {
                label: t('assignment_board_legend_queued'),
                color: BUCKET_COLORS.queued,
              },
            ] as const
          ).map((leg) => (
            <Box
              key={leg.label}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: 0.75,
                  bgcolor: leg.color,
                }}
              />
              <Typography fontSize={11.5} sx={{ color: textMuted }}>
                {leg.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Combined proximity/congestion table — one column per excavator or
          location, each just listing its own trucks top to bottom. */}
      {matrixColumns.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography
            fontSize={14}
            fontWeight={700}
            sx={{ color: textPrimary, mb: 1.25 }}
          >
            {t('assignment_board_matrix_section_title')}
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${borderColor}`,
              bgcolor: panelBg,
              overflowX: 'auto',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {matrixColumns.map((col, colIdx) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color:
                          col.type === 'location'
                            ? col.congested
                              ? theme.palette.error.main
                              : theme.palette.success.main
                            : textPrimary,
                        borderBottom: `1px solid ${borderColor}`,
                        borderLeft:
                          colIdx > 0 ? `2px solid ${borderColor}` : 'none',
                      }}
                    >
                      {col.type === 'location'
                        ? t('assignment_board_matrix_location_header', {
                            name: col.label,
                            count: col.truckCount,
                            capacity: col.trucksCapacity,
                          })
                        : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixRowCount === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={matrixColumns.length}
                      sx={{ color: textMuted, fontSize: 12.5, border: 'none' }}
                    >
                      {t('assignment_board_matrix_empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  Array.from({ length: matrixRowCount }).map((_, rowIdx) => (
                    <TableRow key={rowIdx}>
                      {matrixColumns.map((col, colIdx) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            fontSize: 12.5,
                            color: textPrimary,
                            borderBottom: `1px solid ${borderColor}`,
                            borderLeft:
                              colIdx > 0 ? `2px solid ${borderColor}` : 'none',
                          }}
                        >
                          {col.trucks[rowIdx] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Loading — only blocks the board while we have neither the initial
          snapshot nor any SSE data yet; live pubsub updates must never be
          hidden behind a slow/stuck REST call. */}
      {loading && trucks.length === 0 && (
        <Typography fontSize={13} sx={{ color: textMuted, py: 4 }}>
          {t('assignment_board_loading')}
        </Typography>
      )}

      {/* Board — both directions side by side on one screen instead of behind a tab */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {renderSection(t('assignment_board_dir_haul'), haulGroups)}
        {renderSection(t('assignment_board_dir_travel'), travelGroups)}
      </Box>
    </Box>
  );
};

const AssignmentBoardView: React.FC = () => (
  <CustomMuiThemeProvider>
    <AssignmentBoardInner />
  </CustomMuiThemeProvider>
);

export default AssignmentBoardView;
